// pages/seller/MyProducts.tsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSellerProducts } from "../../hooks/useSellerProducts";
import ProductCard from "../Seller/ProductCard";
import EditProductModal from "../../components/Seller/EditProductModal";
import {
  Package,
  Grid,
  List,
  Plus,
  AlertCircle,
  CheckCircle,
  XCircle,
} from "lucide-react";

const MyProducts: React.FC = () => {
  const navigate = useNavigate();
  const {
    products,
    loading,
    error,
    deleteProduct,
    updateStock,
    updateProduct,
    fetchProducts,
  } = useSellerProducts();
  const [filter, setFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Safety check: ensure products is an array
  const productsArray = Array.isArray(products) ? products : [];

  const filteredProducts = productsArray.filter((product) => {
    if (filter === "all") return true;
    if (filter === "pending_approval")
      return product.status === "PENDING_APPROVAL";
    return product.status === filter;
  });

  const stats = {
    total: productsArray?.length,
    pending: productsArray.filter((p) => p.status === "PENDING_APPROVAL")
      ?.length,
    approved: productsArray.filter((p) => p.status === "APPROVED")?.length,
    rejected: productsArray.filter((p) => p.status === "REJECTED")?.length,
  };

  const handleEditProduct = (product: any) => {
    console.log("Editing product:", product);
    setSelectedProduct(product);
    setIsEditModalOpen(true);
  };

  const handleUpdateProduct = async (productId: string, formData: FormData) => {
    try {
      await updateProduct(productId, formData);
      setIsEditModalOpen(false);
      setSelectedProduct(null);
      // Refresh products to get updated data
      await fetchProducts();
      alert("Product updated successfully!");
    } catch (err) {
      alert("Failed to update product. Please check the console for details.");
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      const success = await deleteProduct(productId);
      if (success) {
        alert("Product deleted successfully!");
      }
    }
  };

  const handleUpdateStock = async (
    productId: string,
    variantId: string,
    stock: number,
  ) => {
    const success = await updateStock(productId, variantId, stock);
    if (success) {
      alert("Stock updated successfully!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">
            Loading your products...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 mt-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                My Products
              </h1>
              <p className="mt-2 text-gray-600">
                Manage your product catalog and inventory with ease
              </p>
            </div>
            <Link
              to="/add-product"
              className="inline-flex items-center px-6 py-3 bg-green-950 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              <Plus size={20} className="mr-2" />
              Add New Product
            </Link>
          </div>

          {/* Stats Cards */}
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-5 border border-gray-100 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">
                    Total Products
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {stats.total}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Package size={24} className="text-white" />
                </div>
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-5 border border-gray-100 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600 font-medium">Pending</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {stats.pending}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                  <AlertCircle size={24} className="text-white" />
                </div>
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-5 border border-gray-100 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-600 font-medium">
                    Approved
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {stats.approved}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-green-500 rounded-xl flex items-center justify-center shadow-lg">
                  <CheckCircle size={24} className="text-white" />
                </div>
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-5 border border-gray-100 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600 font-medium">Rejected</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {stats.rejected}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                  <XCircle size={24} className="text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {[
              { id: "all", label: "All Products" },
              { id: "PENDING_APPROVAL", label: "Pending" },
              { id: "APPROVED", label: "Approved" },
              { id: "REJECTED", label: "Rejected" },
            ].map((filterOption) => (
              <button
                key={filterOption.id}
                onClick={() => setFilter(filterOption.id)}
                className={`px-5 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                  filter === filterOption.id
                    ? "bg-green-950 text-white shadow-lg transform scale-105"
                    : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                }`}
              >
                {filterOption.label}
              </button>
            ))}
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-2 bg-white rounded-xl shadow-md p-1 border border-gray-200">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2.5 rounded-lg transition-all duration-200 ${
                viewMode === "grid"
                  ? "bg-green-950 text-white shadow-md"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              }`}
              title="Grid view"
            >
              <Grid size={20} />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2.5 rounded-lg transition-all duration-200 ${
                viewMode === "list"
                  ? "bg-green-950 text-white shadow-md"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              }`}
              title="List view"
            >
              <List size={20} />
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-xl shadow-sm">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Products Grid/List */}
        {filteredProducts.length > 0 ? (
          <div
            className={
              viewMode === "grid"
                ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                : "space-y-4"
            }
          >
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onDelete={handleDeleteProduct}
                onUpdateStock={handleUpdateStock}
                onEdit={handleEditProduct}
                viewMode={viewMode}
                onProductUpdate={fetchProducts}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center border border-gray-100">
            <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto">
              <Package size={40} className="text-gray-400" />
            </div>
            <h3 className="mt-6 text-xl font-semibold text-gray-900">
              No products found
            </h3>
            <p className="mt-2 text-gray-500">
              Get started by adding your first product to your catalog.
            </p>
            <div className="mt-6">
              <button
                onClick={() => navigate("/add-product")}
                className="inline-flex items-center px-6 py-3 bg-green-950 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                <Plus size={20} className="mr-2" />
                Add New Product
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Product Modal */}
      {isEditModalOpen && selectedProduct && (
        <EditProductModal
          product={selectedProduct}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedProduct(null);
          }}
          onUpdate={handleUpdateProduct}
        />
      )}
    </div>
  );
};

export default MyProducts;
